import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import { SearchSelect } from "../../../shared/components/SearchSelect";
import { getAvisosPago, revisarAvisoPago, deleteAvisoPago } from "../services/portalService";
import {
  ESTILOS_LISTA, StatCard, PestanasEstado, Desplegable, RangoFechas, Buscador,
  SelectorVista, BarraResultados, Pagination, Vacio, Cargando, EstadoBadge, ModalOverlay,
  fechaLarga, desdeHace, labelSt, fldStyle, metaSt, colLabelSt,
} from "../components/adminListaUI";
import styles from "../../finance/invoices/styles/invoice.module.css";

const PAGE_SIZE = 10;

const ESTADOS = {
  PENDIENTE: { bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.3)",  text: "#f59e0b",  icon: "fa-clock",        label: "Pendiente"  },
  VALIDO:    { bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.3)",  text: "#10b981",  icon: "fa-circle-check", label: "Válido"     },
  NO_VALIDO: { bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)", text: "#f87171",  icon: "fa-circle-xmark", label: "No válido"  },
};
const ESTADO_DESCONOCIDO = { bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.3)", text: "#9ca3af", icon: "fa-circle" };

function cfgEstado(estado) {
  return ESTADOS[estado] || { ...ESTADO_DESCONOCIDO, label: estado };
}

/** Solo la fecha de un datetime ISO, sin convertir zonas. */
function soloFecha(iso) {
  return (iso || "").split("T")[0];
}

const OPCIONES_ORDEN = [
  ["recientes", "Más recientes"],
  ["antiguas",  "Más antiguos"],
  ["unidad",    "Unidad (A-Z)"],
];

export default function AdminAvisosPago() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();

  const [avisos,   setAvisos]   = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filtro,   setFiltro]   = useState("PENDIENTE");
  const [saving,   setSaving]   = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [filterFilial, setFilterFilial] = useState("");
  const [filterDesde,  setFilterDesde]  = useState("");
  const [filterHasta,  setFilterHasta]  = useState("");
  const [busqueda,     setBusqueda]     = useState("");
  const [orden,        setOrden]        = useState("recientes");
  const [vista,        setVista]        = useState("lista");
  const [expandido,    setExpandido]    = useState(null);
  const [page,         setPage]         = useState(1);
  const [rechazo,    setRechazo]    = useState(null); // aviso que se está rechazando
  const [motivo,     setMotivo]     = useState("");
  const [motivoErr,  setMotivoErr]  = useState("");

  function load() {
    getAvisosPago().then(d => setAvisos(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/").then(r => r.json()).then(d => setFiliales(d.results || d)).catch(() => {});
  }, []);

  // Si un residente manda un aviso de pago nuevo mientras estamos acá, se
  // actualiza la lista sola en vez de esperar a que alguien la recargue a mano.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "PAGO") load();
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, []);

  async function handleRevisar(id, estado, notasAdmin = "") {
    setSaving(id);
    try {
      await revisarAvisoPago(id, estado, notasAdmin);
      showToast(estado === "VALIDO" ? t("portal.marcadoValido") : t("portal.marcadoNoValido"), estado === "VALIDO");
      setRechazo(null); setMotivo(""); setMotivoErr("");
      load();
    } catch (e) { showToast(e.message || t("common.errorGuardar"), false); }
    finally { setSaving(null); }
  }

  // El rechazo pide motivo: sin él el residente no sabe qué corregir.
  function confirmarRechazo() {
    const texto = motivo.trim();
    if (!texto) { setMotivoErr("Escribí el motivo del rechazo."); return; }
    handleRevisar(rechazo.id, "NO_VALIDO", texto);
  }

  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    try { await deleteAvisoPago(confirm.id); showToast(t("common.eliminado"), true); setConfirm(null); load(); }
    catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  // ── Conteos, filtrado y orden ───────────────────────────────────────────
  const conteos = useMemo(() => ({
    TODOS:     avisos.length,
    PENDIENTE: avisos.filter(a => a.estado === "PENDIENTE").length,
    VALIDO:    avisos.filter(a => a.estado === "VALIDO").length,
    NO_VALIDO: avisos.filter(a => a.estado === "NO_VALIDO").length,
  }), [avisos]);

  // La primera opción ("") es el "sin filtro": el campo muestra "Todas las
  // filiales" en vez del placeholder y se puede volver atrás sin otro botón.
  const opcionesFilial = useMemo(() => [
    { id: "", primary: "Todas las filiales" },
    ...filiales.map(f => ({
      id: String(f.id),
      primary: `Filial ${f.idFilial}`,
      secondary: f.propietario_nombre || "",
    })),
  ], [filiales]);

  const fechasActivas = [filterDesde, filterHasta].filter(Boolean).length;
  const filtrosActivos = filterFilial ? 1 : 0;

  const filtered = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    const lista = avisos.filter(a => {
      if (filtro !== "TODOS" && a.estado !== filtro) return false;
      if (filterFilial && String(a.filial) !== String(filterFilial)) return false;
      const fecha = soloFecha(a.created_at);
      if (filterDesde && fecha < filterDesde) return false;
      if (filterHasta && fecha > filterHasta) return false;
      if (texto) {
        const heno = `${a.filial_nombre || ""} ${a.usuario_nombre || ""} ${a.detalle || ""}`.toLowerCase();
        if (!heno.includes(texto)) return false;
      }
      return true;
    });
    const porCreado = (x, y) => String(x.created_at || "").localeCompare(String(y.created_at || ""));
    return lista.sort({
      recientes: (x, y) => porCreado(y, x),
      antiguas:  porCreado,
      unidad:    (x, y) => String(x.filial_nombre || "").localeCompare(String(y.filial_nombre || "")) || porCreado(y, x),
    }[orden] || ((x, y) => porCreado(y, x)));
  }, [avisos, filtro, filterFilial, filterDesde, filterHasta, busqueda, orden]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Si al filtrar la página actual se queda sin filas, volver a la primera.
  useEffect(() => {
    if (page > 1 && (page - 1) * PAGE_SIZE >= filtered.length) setPage(1);
  }, [filtered.length, page]);

  function limpiarTodo() {
    setFilterFilial(""); setFilterDesde(""); setFilterHasta(""); setBusqueda(""); setPage(1);
  }

  const PESTANAS = [
    { key: "PENDIENTE", label: "Pendiente", color: "#f59e0b" },
    { key: "VALIDO",    label: "Válido",    color: "#10b981" },
    { key: "NO_VALIDO", label: "No válido", color: "#ef4444" },
    { key: "TODOS",     label: t("portal.todos"), color: "#f97316" },
  ];

  // ── Piezas de una fila ──────────────────────────────────────────────────
  // Son funciones que devuelven elementos, NO componentes: declaradas adentro
  // de la página, React las vería como un tipo distinto en cada render y
  // remontaría la lista entera con cada tecla del buscador.

  function accionesDe(a) {
    const guardando = saving === a.id;
    return (
      <div className="pla-acciones">
        {a.comprobante_url && (
          <a href={a.comprobante_url} target="_blank" rel="noreferrer" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.35)",
            color: "#f97316", padding: "8px 15px", borderRadius: 9,
            textDecoration: "none", fontWeight: 700, fontSize: 12.5, whiteSpace: "nowrap",
          }}>
            <i className="fa-solid fa-paperclip" /> Ver comprobante
          </a>
        )}
        {a.estado === "PENDIENTE" && (
          <>
            <button onClick={() => { setRechazo(a); setMotivo(""); setMotivoErr(""); }} disabled={guardando} style={{
              padding: "8px 15px", borderRadius: 9,
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
              color: "#f87171", fontWeight: 700, fontSize: 12.5, cursor: guardando ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
              <i className="fa-solid fa-xmark" /> {t("portal.noValido")}
            </button>
            <button onClick={() => handleRevisar(a.id, "VALIDO")} disabled={guardando} style={{
              padding: "8px 15px", borderRadius: 9,
              background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.35)",
              color: "#10b981", fontWeight: 700, fontSize: 12.5,
              cursor: guardando ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
              {guardando
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                : <><i className="fa-solid fa-check" /> {t("portal.valido")}</>}
            </button>
          </>
        )}
      </div>
    );
  }

  /** Eliminar va como ícono suelto: con tres botones con texto al lado, el
   *  cuarto se partía a una segunda línea y la fila quedaba desordenada. */
  function botonEliminar(a) {
    return (
      <button
        onClick={() => setConfirm({ id: a.id, name: `${a.filial_nombre} - ${soloFecha(a.created_at)}` })}
        title="Eliminar aviso"
        style={{
          width: 34, height: 34, flexShrink: 0, borderRadius: 9, cursor: "pointer",
          background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
          color: "#f87171", fontSize: 12.5,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <i className="fa-solid fa-trash" />
      </button>
    );
  }

  /** Detalle que abre el chevron: lo que no cabe en la fila. */
  function detalleDe(a) {
    const campos = [
      ["Enviado", a.created_at ? `${fechaLarga(a.created_at)} · ${desdeHace(a.created_at)}` : "-"],
      a.revisado_por_nombre
        ? ["Revisado por", `${a.revisado_por_nombre}${a.revisado_at ? ` · ${fechaLarga(a.revisado_at)}` : ""}`]
        : null,
    ].filter(Boolean);

    return (
      <div style={{
        borderTop: "1px dashed var(--border-card)", marginTop: 12, paddingTop: 12,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "8px 18px" }}>
          {campos.map(([k, v]) => (
            <div key={k}>
              <div style={colLabelSt}>{k}</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)", marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
        {a.detalle && (
          <div>
            <div style={colLabelSt}>Descripción del residente</div>
            <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {a.detalle}
            </p>
          </div>
        )}
        {a.notas_admin && (
          <div style={{
            background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.25)",
            borderRadius: 9, padding: "9px 12px",
          }}>
            <div style={{ ...colLabelSt, color: "#f87171" }}>Motivo del rechazo</div>
            <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {a.notas_admin}
            </p>
          </div>
        )}
      </div>
    );
  }

  function filaDe(a, ultima) {
    const abierto = expandido === a.id;
    return (
      <div key={a.id} style={{
        padding: "14px 18px",
        borderBottom: ultima && !abierto ? "none" : "1px solid var(--border-card)",
        background: abierto ? "var(--hover-bg)" : "transparent",
        transition: "background .12s",
      }}>
        <div className="avp-row">
          {/* Unidad + estado + quién y cuándo */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11, flexShrink: 0,
              background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#f97316", fontSize: 15,
            }}>
              <i className="fa-solid fa-building" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: "var(--text-primary)" }}>{a.filial_nombre}</span>
                <EstadoBadge cfg={cfgEstado(a.estado)} />
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                {a.usuario_nombre && (
                  <span style={metaSt}>
                    <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.6 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.usuario_nombre}</span>
                  </span>
                )}
                <span style={metaSt}>
                  <i className="fa-solid fa-calendar" style={{ fontSize: 10, opacity: 0.6 }} />
                  {soloFecha(a.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* Qué dice el residente */}
          <div style={{ minWidth: 0 }}>
            {a.detalle ? (
              <p style={{
                margin: 0, fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5,
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
              }} title={a.detalle}>
                {a.detalle}
              </p>
            ) : (
              <span style={{ fontSize: 12.5, color: "var(--text-muted)", fontStyle: "italic" }}>Sin descripción</span>
            )}
            {a.notas_admin && (
              <p style={{
                margin: "4px 0 0", fontSize: 12, color: "#f87171",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={a.notas_admin}>
                <i className="fa-solid fa-comment" style={{ marginRight: 5, fontSize: 10 }} />{a.notas_admin}
              </p>
            )}
          </div>

          {/* Acciones + desplegar */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            {accionesDe(a)}
            {botonEliminar(a)}
            <button onClick={() => setExpandido(abierto ? null : a.id)}
              title={abierto ? "Ocultar detalle" : "Ver detalle"} style={{
                width: 34, height: 34, flexShrink: 0, borderRadius: 9, cursor: "pointer",
                background: "transparent", border: "1px solid var(--border-card)",
                color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              <i className={`fa-solid fa-chevron-${abierto ? "up" : "down"}`} style={{ fontSize: 11 }} />
            </button>
          </div>
        </div>

        {abierto && detalleDe(a)}
      </div>
    );
  }

  function tarjetaDe(a) {
    const abierto = expandido === a.id;
    return (
      <div key={a.id} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 13, padding: "15px 16px", display: "flex", flexDirection: "column", gap: 11,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center", color: "#f97316", fontSize: 14,
          }}>
            <i className="fa-solid fa-building" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15.5, color: "var(--text-primary)" }}>{a.filial_nombre}</div>
            <div style={{ marginTop: 5 }}><EstadoBadge cfg={cfgEstado(a.estado)} /></div>
          </div>
          <button onClick={() => setExpandido(abierto ? null : a.id)} style={{
            width: 30, height: 30, flexShrink: 0, borderRadius: 8, cursor: "pointer",
            background: "transparent", border: "1px solid var(--border-card)",
            color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className={`fa-solid fa-chevron-${abierto ? "up" : "down"}`} style={{ fontSize: 10 }} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {a.usuario_nombre && (
            <span style={metaSt}>
              <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.6, width: 13 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.usuario_nombre}</span>
            </span>
          )}
          <span style={metaSt}>
            <i className="fa-solid fa-calendar" style={{ fontSize: 10, opacity: 0.6, width: 13 }} />
            {fechaLarga(a.created_at)}
          </span>
        </div>

        {a.detalle && (
          <p style={{
            margin: 0, fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.5,
            background: "var(--hover-bg)", borderRadius: 8, padding: "8px 11px",
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
          }} title={a.detalle}>
            {a.detalle}
          </p>
        )}

        <div style={{
          borderTop: "1px solid var(--border-card)", paddingTop: 11,
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>{accionesDe(a)}</div>
          {botonEliminar(a)}
        </div>
        {abierto && detalleDe(a)}
      </div>
    );
  }

  return (
    <div className="portal-admin">
      {toastEl}
      <style>{`
        ${ESTILOS_LISTA}
        .avp-row { display:grid; grid-template-columns:minmax(230px,1fr) minmax(170px,1.2fr) auto;
                   gap:16px; align-items:center; }
        /* En anchos medios la descripción no se oculta — es lo principal de un
           aviso — sino que baja a una segunda línea a todo lo ancho, y arriba
           quedan la unidad y las acciones. */
        @media (max-width:1180px){
          .avp-row { grid-template-columns:1fr auto; gap:12px 16px; }
          .avp-row > div:nth-child(2) { grid-column:1 / -1; order:3; }
        }
        @media (max-width:900px){
          .avp-row { grid-template-columns:1fr; }
          .avp-row > div:nth-child(2) { grid-column:auto; order:0; }
        }
      `}</style>

      <ConfirmModal open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting} />

      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Portal Admin /</span>
        </Link>
        <span className={styles.active}> {t("portal.avisosPago")}</span>
      </span>

      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h2 className="mt-2 p-0 title">{t("portal.avisosPago")}</h2>
        <p style={{ margin: 0, color: "var(--text-muted)" }}>{t("portal.adminAvisosPagoDesc")}</p>
      </div>

      {/* Resumen */}
      {!loading && avisos.length > 0 && (
        <div className="pla-stats">
          <StatCard icon="fa-inbox"        label="Total recibidos" value={conteos.TODOS}
            total={conteos.TODOS} color="#f97316" mostrarPorcentaje={false} />
          <StatCard icon="fa-clock"        label="Pendientes" value={conteos.PENDIENTE} total={conteos.TODOS} color="#f59e0b" />
          <StatCard icon="fa-circle-check" label="Válidos"    value={conteos.VALIDO}    total={conteos.TODOS} color="#10b981" />
          <StatCard icon="fa-circle-xmark" label="No válidos" value={conteos.NO_VALIDO} total={conteos.TODOS} color="#ef4444" />
        </div>
      )}

      <PestanasEstado
        opciones={PESTANAS}
        valor={filtro}
        conteos={conteos}
        onChange={k => { setFiltro(k); setPage(1); setExpandido(null); }}
      />

      {/* Buscador + filtros + vista */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 14, overflow: "hidden",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border-card)" }}>
          <div className="pla-toolbar">
            <Buscador
              valor={busqueda}
              onChange={v => { setBusqueda(v); setPage(1); }}
              placeholder="Buscar por unidad, residente o descripción…"
            />
            <div className="pla-tools">
              <Desplegable icono="fa-calendar-days" activos={fechasActivas} ancho={380}
                etiqueta={fechasActivas ? `${filterDesde || "…"} → ${filterHasta || "…"}` : "Selecciona fechas"}>
                <RangoFechas
                  titulo="Fecha de envío"
                  desde={filterDesde} hasta={filterHasta}
                  onDesde={v => { setFilterDesde(v); setPage(1); }}
                  onHasta={v => { setFilterHasta(v); setPage(1); }}
                  onLimpiar={() => { setFilterDesde(""); setFilterHasta(""); setPage(1); }}
                />
              </Desplegable>

              <Desplegable icono="fa-filter" etiqueta="Filtros" activos={filtrosActivos} ancho={300}>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={labelSt}>Filial</label>
                    <SearchSelect
                      options={opcionesFilial}
                      value={filterFilial}
                      onChange={id => { setFilterFilial(id); setPage(1); }}
                      placeholder="Todas las filiales"
                      icon="fa-house-chimney"
                    />
                  </div>
                  {filtrosActivos > 0 && (
                    <button onClick={() => { setFilterFilial(""); setPage(1); }} style={{
                      padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                      background: "transparent", border: "1px solid var(--border-card)",
                      color: "var(--text-secondary)", fontSize: 12, fontWeight: 600,
                    }}>
                      Quitar filtros
                    </button>
                  )}
                </div>
              </Desplegable>

              <SelectorVista vista={vista} onChange={setVista} />
            </div>
          </div>
        </div>

        <BarraResultados
          texto={`${filtered.length} ${filtered.length === 1 ? "aviso" : "avisos"}`}
          hayFiltros={filtrosActivos > 0 || fechasActivas > 0 || !!busqueda}
          onLimpiar={limpiarTodo}
          orden={orden}
          onOrden={setOrden}
          opcionesOrden={OPCIONES_ORDEN}
        />

        {/* Listado */}
        {loading ? (
          <Cargando texto="Cargando avisos…" />
        ) : filtered.length === 0 ? (
          <Vacio
            icono="fa-inbox"
            titulo={avisos.length === 0 ? t("portal.sinAvisosPago") : "Sin resultados"}
            detalle={avisos.length === 0
              ? "No hay avisos de pago enviados por residentes."
              : "Probá cambiando los filtros o la búsqueda."}
          />
        ) : vista === "tarjetas" ? (
          <>
            <div className="pla-grid">{paged.map(a => tarjetaDe(a))}</div>
            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
          </>
        ) : (
          <>
            {paged.map((a, i) => filaDe(a, i === paged.length - 1))}
            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
          </>
        )}
      </div>

      {/* ── Motivo del rechazo ── */}
      {rechazo && (
        <ModalOverlay width={460} onClose={() => saving !== rechazo.id && setRechazo(null)}>
          <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)" }}>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>
              Rechazar aviso de pago
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
              {rechazo.filial_nombre} · {fechaLarga(rechazo.created_at)}
            </p>
          </div>
          <div style={{ padding: "20px 24px" }}>
            <label style={labelSt}>
              Motivo <span style={{ color: "#f87171" }}>*</span>
            </label>
            <textarea
              value={motivo}
              onChange={e => { setMotivo(e.target.value); if (motivoErr) setMotivoErr(""); }}
              rows={3}
              placeholder="Ej: el comprobante no se ve, el monto no coincide, la fecha no corresponde…"
              style={fldStyle({
                resize: "vertical",
                border: `1px solid ${motivoErr ? "#f87171" : "var(--border-card)"}`,
              })}
            />
            <p style={{ margin: "6px 0 0", fontSize: 12, color: motivoErr ? "#f87171" : "var(--text-secondary)" }}>
              {motivoErr || "El residente va a ver este motivo en su notificación."}
            </p>
          </div>
          <div style={{
            padding: "16px 24px 20px", borderTop: "1px solid var(--border-card)",
            display: "flex", gap: 10, justifyContent: "flex-end",
          }}>
            <button onClick={() => setRechazo(null)} style={{
              padding: "9px 20px", borderRadius: 8, border: "1px solid var(--border-card)",
              background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
            }}>
              {t("common.cancelar")}
            </button>
            <button onClick={confirmarRechazo} disabled={saving === rechazo.id} style={{
              padding: "9px 22px", borderRadius: 8, border: "none",
              background: "#ef4444", color: "#fff", fontWeight: 700, fontSize: 14,
              cursor: saving === rechazo.id ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}>
              {saving === rechazo.id
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> Enviando…</>
                : <><i className="fa-solid fa-xmark" /> Rechazar</>}
            </button>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
